// All or portions of this file Copyright © 2019 Amazon Web Services, Inc. or its affiliates or licensors.
// All rights reserved. For full license terms and conditions, please see the LICENSE file at the root of
// this distribution (the "License") or go to https://content.sumerian.amazonaws.com/engine/latest/license.txt.
// All use of the software is governed by the License. All materials are made available on an "AS IS" BASIS, WITHOUT
// WARRANTIES OR CONDITIONS OF ANY KIND, express or implied. Do not remove or modify any license notices.
class AudioProcessor {
  /**
   * The AudioProcessor class
   * Contains a collection of audio processing utilities for downsampling and
   * encoding audio.
   * @param {ArrayBuffer} buffer The raw audio data.
   * @param {Number} sampleRate The current sample rate for the provided audio.
   */
  constructor(buffer, sampleRate) {
    this._buffer = buffer;
    this._sampleRate = sampleRate;
  }

  /**
   * Downsamples the audio to a target sample rate.
   *
   * Inspired by the following blog post from the Lex team:
   * https://aws.amazon.com/blogs/machine-learning/capturing-voice-input-in-a-browser/
   */
  downsampleAudio(targetSampleRate) {
    if (!this._buffer || !this._buffer.length) {
      console.warn('[Sumerian] Audio buffer is empty.');
      return;
    }

    if (this._sampleRate === targetSampleRate) {
      return this._buffer;
    }

    if (this._sampleRate < targetSampleRate) {
      throw new Error(`[Sumerian] Sample rate is less than ${targetSampleRate}.`);
    }

    const bufferLength = this._buffer.length;
    const sampleRateRatio = this._sampleRate / targetSampleRate;
    const newLength = Math.round(bufferLength / sampleRateRatio);

    const downsampledBuffer = new Float32Array(newLength);

    let position = 0;
    let bufferOffset = 0;
    while (position < newLength) {
      const nextBufferOffset = Math.round((position + 1) * sampleRateRatio);

      let accumulator = 0;
      let count = 0;
      for (let i = bufferOffset; i < nextBufferOffset && i < bufferLength; i++) {
        accumulator += this._buffer[i];
        count++;
      }

      downsampledBuffer[position] = accumulator / count;
      position++;
      bufferOffset = nextBufferOffset;
    }

    return downsampledBuffer;
  }

  /**
   * Converts audio data to WAV.
   *
   * Inspired by the following blog post from the Lex team:
   * https://aws.amazon.com/blogs/machine-learning/capturing-voice-input-in-a-browser/
   */
  encodeWAV(buffer, targetSampleRate) {
    if (!buffer) {
      return;
    }

    const encodedBuffer = new ArrayBuffer(44 + buffer.length * 2);
    const view = new DataView(encodedBuffer);

    this._writeString(view, 0, 'RIFF');
    view.setUint32(4, 32 + buffer.length * 2, true);
    this._writeString(view, 8, 'WAVE');
    this._writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true);
    view.setUint32(24, targetSampleRate, true);
    view.setUint32(28, targetSampleRate * 2, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);
    this._writeString(view, 36, 'data');
    view.setUint32(40, buffer.length * 2, true);

    return view;
  }

  _writeString(view, offset, string) {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  }

  /**
   * Encode WAV audio as 16bit PCM
   *
   * @param {DataView} view The WAV audio
   * @param {number} offset The starting offset to use for encoding
   * @param {ArrayBuffer} input The original audio buffer data.
   */
  floatTo16BitPCM(view, offset, input) {
    if(input) {
      for (let i = 0; i < input.length; i++, offset += 2) {
        const s = Math.max(-1, Math.min(1, input[i]));
        view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
      }
    }
  }
}

export default AudioProcessor;
